import { createApi } from "@reduxjs/toolkit/query/react";
import { format } from "date-fns";

import { ISO8601_DATE_FORMAT } from "@constants";

import { baseQuery } from "../../../shared/api/base-query";
import { PagedResponseDto } from "../../../shared/dtos/paged-response.dto";
import { OrderGroup } from "../../../shared/interface";
import { buildFilterCondition, buildFilterExpression } from "../../../shared/utils";
import { TObservationsRequest } from "../containers/observations-list/observations-list-utils";
import { IObservationDto } from "../shared/dtos/observation.dto";
import { Observation } from "../shared/models/observation";

const observationsUrl = "ObservationsV3";

export const observationsApiSlice = createApi({
  reducerPath: "api-observations",
  baseQuery,
  tagTypes: ["ObservationsList"],
  endpoints: (builder) => ({
    observationsList: builder.query<
      { items: Observation[]; totalCount: number },
      TObservationsRequest
    >({
      query: (request: TObservationsRequest) => {
        const { paginationState, onlyOriginal } = request;
        const { limit, offset, order } = paginationState;
        const pageNumber = Math.floor(offset / limit) + 1;
        const sort = order.length > 0 ? buildSortString(order) : undefined;
        const filter = buildObservationsFilter(request);
        return {
          url: observationsUrl,
          params: {
            pageNumber,
            pageSize: limit,
            sort,
            filter,
            ...(onlyOriginal !== undefined && { onlyOriginal }),
          },
          method: "GET",
        };
      },
      transformResponse: (response: PagedResponseDto<IObservationDto>) => ({
        items: response.data.map((dto) => Observation.fromDto(dto)),
        totalCount: response.meta.totalCount,
      }),
      providesTags: ["ObservationsList"],
    }),

    removeObservation: builder.mutation<void, string>({
      query: (id) => ({
        url: `${observationsUrl}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ObservationsList"],
    }),
  }),
});

function buildObservationsFilter(request: TObservationsRequest): string {
  const { filtersState, seasonId, farmId } = request;
  const { from, to, cropType, farmLandId, phenoPhaseId } = filtersState;

  return buildFilterExpression(
    buildFilterCondition("seasonId", "==", seasonId),
    buildFilterCondition("farmId", "==", farmId),
    from &&
      buildFilterCondition("observationDate", ">=", format(from, ISO8601_DATE_FORMAT)),
    to && buildFilterCondition("observationDate", "<=", format(to, ISO8601_DATE_FORMAT)),
    cropType?.id && buildFilterCondition("cropTypeId", "==", cropType.id),
    farmLandId && buildFilterCondition("farmLandId", "==", farmLandId),
    phenoPhaseId && buildFilterCondition("phenoPhaseId", "==", phenoPhaseId)
  );
}

function buildSortString(order: OrderGroup): string {
  return order.map(([field, direction]) => `${String(field)} ${direction}`).join(", ");
}

export const { useObservationsListQuery, useRemoveObservationMutation } =
  observationsApiSlice;
